/**
 * Hanging power cables (see types.ts's `Cable`).
 *
 * Same shape as `road-mesh.ts`: an authored polyline becomes generated
 * geometry, rebuilt wholesale rather than transformed, and exposed through
 * `SceneHandles` so the editor can preview it live.
 *
 * WHY A PARABOLA AND NOT A TRUE CATENARY
 * A real hanging cable is a catenary, `y = a·cosh(x/a)`. Solving for `a` given
 * a span length and a target sag needs a Newton iteration on a transcendental
 * equation, which brings a convergence failure mode into a decorative visual.
 * At the sag-to-span ratios used here (well under 1:10) a parabola and a
 * catenary differ by a fraction of a percent of the sag — far below a pixel at
 * this camera distance. So this samples a parabola, deliberately and
 * explicitly, rather than pretending to be physical.
 *
 * The one thing that DOES matter visually is that sag scales with span length:
 * a fixed droop makes short spans look slack and long spans look tight, which
 * reads instantly as wrong even when nobody can say why.
 */
import * as THREE from 'three';
import type { Cable, CableAnchor } from '../types';

/** Metres of droop at mid-span, for a span of `SAG_REFERENCE_SPAN`. */
export const DEFAULT_CABLE_SAG = 0.55;
/** Span length the default sag is quoted against; longer spans droop more. */
const SAG_REFERENCE_SPAN = 12;
export const DEFAULT_CABLE_RADIUS = 0.035;
/** Weathered dark cable — near-black with a touch of warmth, not pure #000. */
export const DEFAULT_CABLE_COLOR = 0x241f1a;
/** Samples per span. 12 is smooth at this thickness; more is wasted triangles. */
const SEGMENTS_PER_SPAN = 12;
/** Radial slices around the tube. A cable is thin — 5 reads as round. */
const RADIAL_SEGMENTS = 5;

/**
 * Points along one hanging span, sampled from the parabola.
 *
 * Pure and exported so the droop is unit-testable: a cable with the sag
 * applied upward, or not applied at all, still renders a perfectly plausible
 * line and nobody notices from a screenshot.
 */
export function spanPoints(
  a: CableAnchor,
  b: CableAnchor,
  sag: number,
  segments = SEGMENTS_PER_SPAN,
): Array<{ x: number; y: number; z: number }> {
  const dx = b.position.x - a.position.x;
  const dz = b.position.z - a.position.z;
  const span = Math.hypot(dx, dz);
  // Scale the authored sag by how long this span actually is.
  const droop = sag * (span / SAG_REFERENCE_SPAN);
  const out: Array<{ x: number; y: number; z: number }> = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    // 4t(1-t) peaks at 1.0 exactly at t = 0.5 and is 0 at both ends, so the
    // cable meets each anchor at its authored height and droops in between.
    const dip = 4 * t * (1 - t) * droop;
    out.push({
      x: a.position.x + dx * t,
      y: a.height + (b.height - a.height) * t - dip,
      z: a.position.z + dz * t,
    });
  }
  return out;
}

/** Every point along a whole cable run, spans joined end to end. */
export function cablePoints(cable: Cable): Array<{ x: number; y: number; z: number }> {
  const sag = cable.sag ?? DEFAULT_CABLE_SAG;
  const out: Array<{ x: number; y: number; z: number }> = [];
  for (let i = 0; i < cable.anchors.length - 1; i++) {
    const pts = spanPoints(cable.anchors[i]!, cable.anchors[i + 1]!, sag);
    // Drop the first point of every span after the first — it duplicates the
    // previous span's last point, and a duplicate makes TubeGeometry emit a
    // zero-length segment with an undefined frame.
    out.push(...(i === 0 ? pts : pts.slice(1)));
  }
  return out;
}

export function buildCableMesh(cable: Cable): THREE.Object3D {
  const pts = cablePoints(cable);
  if (pts.length < 2) {
    throw new Error(`cable "${cable.id}" needs at least 2 anchors, got ${cable.anchors.length}`);
  }
  const curve = new THREE.CatmullRomCurve3(
    pts.map((p) => new THREE.Vector3(p.x, p.y, p.z)),
    false,
    // 'catmullrom' with low tension would round off the pole tops; the sampled
    // points already describe the shape, so this just threads them.
    'catmullrom',
    0.2,
  );
  const geo = new THREE.TubeGeometry(
    curve,
    Math.max(8, pts.length * 2),
    cable.radius ?? DEFAULT_CABLE_RADIUS,
    RADIAL_SEGMENTS,
    false,
  );
  const mat = new THREE.MeshStandardMaterial({
    color: cable.color ?? DEFAULT_CABLE_COLOR,
    roughness: 0.9,
    metalness: 0.1,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = `cable:${cable.id}`;
  mesh.userData.cableId = cable.id;
  // Thin and high up — it should take the settlement's light but casting a
  // shadow map from a 3.5 cm tube is pure cost for nothing visible.
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

/** All cables in one group, in author order. */
export function buildCables(cables: readonly Cable[] | undefined): THREE.Group {
  const group = new THREE.Group();
  group.name = 'cables';
  for (const cable of cables ?? []) {
    if (cable.anchors.length < 2) continue; // an unfinished run, not an error
    group.add(buildCableMesh(cable));
  }
  return group;
}
